import React, { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { MenuItem, MenuList } from "@mui/material";
import {
	createNotification,
	CustomDialog,
	CustomIconButton,
	CustomMenuButton,
	CustomTooltip,
	icon,
	IconComponent,
	VerticalDivider,
} from "../../../../../../common/components";
import styles from "./ModelRow.module.css";
import { translate } from "../../../../../../common/providers";
import LaunchDialog from "./LaunchDialog";
import AgentToolSidePanel from "./AgentToolSidePanel";
import AgentSpawnableSidePanel from "./AgentSpawnableSidePanel";
import { useApi } from "../../../../../../common/hooks";
import { AiAgentsService } from "../../../../../../api";
import { downloadFile } from "../../../../../../common/utils";
import { getAgentModelDetails, resolveExportFilename } from "../../utils";

export default function ModelRow({ agent, onDelete, onLaunch, onOpenEdition, onRefresh }) {
	const { id = null, title, prompt = "", isLinked = false, isDeprecated = false, hasNoTool = false } = agent || {};
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [openLaunchDialog, setOpenLaunchDialog] = useState(false);
	const [openSidePanel, setOpenSidePanel] = useState(false);
	const [openSpawnableSidePanel, setOpenSpawnableSidePanel] = useState(false);
	const menuButtonRef = useRef(null);
	const userId = useSelector(({ context }) => context?.user?.id);
	const { call: duplicateAgent } = useApi(AiAgentsService.duplicateAgent);
	const { call: exportAgent } = useApi(AiAgentsService.exportAgent);
	const modelDetails = getAgentModelDetails(agent);
	const renderModelCell = () => {
		if (modelDetails.isLlmNotFound) {
			return (
				<span className={styles.llmNotFound}>
					<IconComponent color="var(--color-orange)" icon={icon.faExclamationTriangle} size="xs" />
					{translate("dsi.agents.llm-model.not-found")}
				</span>
			);
		}
		if (modelDetails.isDefaultModel) {
			return (
				<span className={styles["text--red"]}>
					{modelDetails.displayName || translate("dsi.agents.dialog.ai-model.default-option")}
				</span>
			);
		}
		return modelDetails.displayName ?? "—";
	};
	const handleOpenDeleteDialog = () => {
		if (menuButtonRef.current) {
			menuButtonRef.current.onClose();
		}
		setOpenDeleteDialog((prev) => !prev);
	};
	const handleDelete = () => {
		if (onDelete && typeof onDelete === "function" && id) {
			onDelete(id);
		}
	};
	const handleOpenEdition = () => {
		if (typeof onOpenEdition === "function" && agent) {
			onOpenEdition(agent);
		}
	};
	const handleOpenLaunchDialog = () => {
		setOpenLaunchDialog((prev) => !prev);
	};
	const handleCloseSidePanel = () => {
		setOpenSidePanel((prev) => !prev);
	};
	const handleCloseSpawnableSidePanel = () => {
		setOpenSpawnableSidePanel((prev) => !prev);
	};
	const handleCopyAgent = () => {
		if (userId) {
			duplicateAgent({ id }).then(onRefresh).catch(console.error);
		}
	};
	const handleExportAgent = () => {
		if (id) {
			if (menuButtonRef.current) {
				menuButtonRef.current.onClose();
			}
			exportAgent({ agentId: id })
				.then(({ data, filename }) => {
					downloadFile({
						data,
						filename: resolveExportFilename(filename, "agent_export.json"),
						filetype: "json",
					});
					createNotification({
						type: "success",
						message: translate("dsi.agents.agent.export.success"),
					});
				})
				.catch((err) => {
					console.error(err);
					createNotification({
						type: "error",
						message: translate("dsi.agents.agent.export.failure"),
					});
				});
		}
	};
	return (
		<div key={id} className={styles.row}>
			<div className={styles.rowId}>{id}</div>
			<div className={styles.textWithIcon}>
				<div className={styles.text__icon} data-has-icon={isLinked}>
					<IconComponent color="var(--blue-500)" icon={icon.faStar} />
				</div>
				<div className={`${styles.cutText} ${styles.name}`}>{title}</div>
			</div>
			<div className={styles.cutText}>{prompt}</div>
			<div className={styles.cutText}>{renderModelCell()}</div>
			<div className={styles.actions}>
				{isDeprecated && (
					<CustomTooltip title={translate("dsi.agents.warning.deprecated")}>
						<IconComponent color="var(--amber-500)" icon={icon.faExclamationTriangle} />
					</CustomTooltip>
				)}
				{hasNoTool && (
					<CustomTooltip title={translate("dsi.agents.warning.no-tool")}>
						<IconComponent color="var(--red-500)" icon={icon.faExclamationTriangle} />
					</CustomTooltip>
				)}
				{modelDetails.isInactive && (
					<CustomTooltip title={translate("dsi.agents.errors.inactive-model")}>
						<IconComponent color="var(--color-orange)" icon={icon.faTriangleExclamation} />
					</CustomTooltip>
				)}
				<CustomIconButton
					icon={icon.faPenToSquare}
					size="sm"
					tooltip={translate("common:btn.edit")}
					variant="outlined"
					onClick={handleOpenEdition}
				/>
				<CustomIconButton
					icon={icon.faWrench}
					iconColor={
						((isDeprecated || hasNoTool) &&
							`var(--color-${(isDeprecated && "orange") || (hasNoTool && "red")})`) ||
						undefined
					}
					size="sm"
					tooltip={translate("dsi.agents.button.edit-tools")}
					variant="outlined"
					onClick={handleCloseSidePanel}
				/>
				<CustomIconButton
					icon={icon.faLink}
					size="sm"
					tooltip={translate("dsi.agents.spawnable")}
					variant="outlined"
					onClick={handleCloseSpawnableSidePanel}
				/>
				<CustomMenuButton
					ref={menuButtonRef}
					text={<IconComponent color="var(--color-black)" icon={icon.faEllipsisH} size="md" />}
					variant="outlined"
				>
					<MenuList className={styles.menuList}>
						{[
							{
								label: translate("common:btn.duplicate"),
								icon: icon.faCopy,
								disabled: isDeprecated || hasNoTool,
								onClick: handleCopyAgent,
							},
							{
								label: translate("dsi.agents.export-btn"),
								icon: icon.faDownload,
								onClick: handleExportAgent,
							},
							{
								label: translate("common:btn.delete"),
								icon: icon.faTrash,
								onClick: handleOpenDeleteDialog,
							},
						].map((filter) => (
							<MenuItem
								key={filter.label}
								className={styles.menuItem}
								disabled={filter.disabled}
								onClick={filter.onClick}
							>
								<div className={styles.menuItem__icon}>
									{filter.icon && (
										<IconComponent color="var(--color-black)" icon={filter.icon} size="sm" />
									)}
								</div>
								<div className={styles.menuItem__text}>{filter.label}</div>
							</MenuItem>
						))}
					</MenuList>
				</CustomMenuButton>
				<VerticalDivider className={styles.verticalDivider} />
				<CustomIconButton
					disabled={isDeprecated || hasNoTool}
					icon={icon.faPlay}
					iconColor={((isDeprecated || hasNoTool) && "var(--grey-400)") || undefined}
					size="sm"
					tooltip={translate(
						((isDeprecated || hasNoTool) &&
							`dsi.agents.warning.${(isDeprecated && "deprecated") || "no-tool"}`) ||
							"common:btn.launch"
					)}
					variant="outlined"
					onClick={handleOpenLaunchDialog}
				/>
			</div>
			<CustomDialog
				iconColor="var(--red-500)"
				iconModel={icon.faExclamationTriangle}
				open={openDeleteDialog}
				submitColor="destructive"
				subTitle={translate("dsi.agents.agent.deletion.warning")}
				title={translate("common:delete-confirmation-popup.title")}
				onClose={handleOpenDeleteDialog}
				onSubmit={handleDelete}
			/>
			<LaunchDialog agent={agent} open={openLaunchDialog} onClose={handleOpenLaunchDialog} onLaunch={onLaunch} />
			<AgentToolSidePanel
				agentId={id}
				agentName={title}
				open={openSidePanel}
				onClose={handleCloseSidePanel}
				onRefresh={onRefresh}
			/>
			<AgentSpawnableSidePanel
				agentId={id}
				agentName={title}
				open={openSpawnableSidePanel}
				onClose={handleCloseSpawnableSidePanel}
				onRefresh={onRefresh}
			/>
		</div>
	);
}
